/**
 * The gate-deletion confirmation — **two independent gates in front of one irreversible call.**
 *
 * `DELETE /gates/{gateId}` destroys the gate, every membership on it (including the caller's own
 * installer grant), every view record, and the BrokerCode claim. Nothing restores it: a re-install
 * is a new `GateId`, a new roster, and a new subscription. That is why this is not a `Popover`
 * with a red button — the two controls here are deliberately of *different kinds*, so no single
 * repeated gesture can carry someone through both:
 *
 *  1. **Type the confirmation word.** Compared after trimming and **case-insensitively**, against
 *     the localized word shown on screen — a Hebrew UI must not silently accept "confirm".
 *  2. **Carry the slider the length of its track.** Unlocked only once step 1 matches.
 *
 * The gate's name is shown but is deliberately *not* the thing to type: a name is often one tap
 * away in the same view and is frequently short, which makes it a weaker gate than a fixed word
 * the user must read an instruction to learn.
 *
 * **Case is not part of the gate, and insisting on it was costing the wrong people.** `Gate and
 * members` is what a phone keyboard autocapitalizes the first word of, so a manager who typed the
 * right phrase met a dead slider with nothing on screen saying why — the input is `aria-invalid`
 * and otherwise silent. What the word actually proves is that the person *read the instruction*,
 * and reading it is unaffected by shift. **This is not D131's rule reached from the other side**:
 * a `BrokerCode` may not be case-folded because its case reaches an MQTT topic and two spellings
 * address two different gates; this string addresses nothing and is compared against a constant
 * this file renders one line above the field. `toLowerCase()` and not `toLocaleLowerCase()`,
 * because the latter reads the *host* locale — Turkish folds `I` to `ı`, which would break the
 * English word on a Turkish phone for a comparison that has no locale of its own.
 */
import { useState } from 'react'
import { SecondaryButton, SlideToConfirm } from '../../components'
import { useTranslation } from '../../i18n/I18nProvider'

export function DeleteGateDialog({
  gateName,
  busy,
  error,
  onCancel,
  onConfirm,
}: {
  readonly gateName: string
  readonly busy: boolean
  /** Rendered under the slider; its presence is the only thing that marks the attempt failed. */
  readonly error?: string
  onCancel(): void
  onConfirm(): void
}): JSX.Element {
  const { t } = useTranslation('gate')
  const { t: tCommon } = useTranslation('common')
  const [typed, setTyped] = useState('')

  const word = t('deleteGateConfirmWord')
  const matched = typed.trim().toLowerCase() === word.toLowerCase()

  return (
    <div className="popover-backdrop" onClick={busy ? undefined : onCancel}>
      <div className="popover" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <h2 className="popover-title">{t('deleteGateTitle')}</h2>
        <p className="popover-body">{t('deleteGateBody', { name: gateName })}</p>
        <label className="delete-gate-prompt" htmlFor="deleteGateConfirm">
          {t('deleteGatePrompt', { word })}
        </label>
        <input
          id="deleteGateConfirm"
          className="input"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          disabled={busy}
          aria-invalid={typed.length > 0 && !matched}
        />
        <SlideToConfirm
          label={t('deleteGateSlide')}
          committedLabel={t('deleteGateSliding')}
          disabled={!matched}
          busy={busy}
          onConfirm={onConfirm}
        />
        {error === undefined ? null : <p className="popover-error">{error}</p>}
        <div className="popover-actions">
          <SecondaryButton onClick={onCancel} disabled={busy}>
            {tCommon('cancel')}
          </SecondaryButton>
        </div>
      </div>
    </div>
  )
}
